import type { OneflowContract } from "@repo/api";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { api } from "~/lib/api";
import { usePermissionManager } from "~/managers/use-permission-manager";
import { usePermissionStore } from "~/stores/use-permission-store";
import { Permissions } from "@repo/database";

export const Route = createFileRoute(
  "/_main/settings/_layout/account/contracts",
)({
  component: ContractsPage,
});

function ContractsPage() {
  const [selectedContract, setSelectedContract] = useState<
    OneflowContract | undefined
  >();
  const { mutate: generateAccessLink } =
    api.contract.generateAccessLink.useMutation();
  const { data } = api.contract.getContracts.useQuery();
  const { data: downloadUrl } = api.contract.downloadContract.useQuery(
    selectedContract?.id.toString(),
    {
      enabled: !!selectedContract,
    },
  );
  const { currentEmployee } = usePermissionStore();
  const { hasPermission } = usePermissionManager();

  const [permission, setPermission] = useState(false);
  const { t } = useTranslation("common");

  const handleGenerateAccessLink = (contractId: string) => {
    generateAccessLink(contractId, {
      onSuccess: (url) => {
        window.open(url, "_blank");
      },
    });
  };

  const columns: ColumnDef<OneflowContract>[] = [
    {
      id: t("Contract.contract_name"),
      accessorKey: "invoiceId",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Contract.contract_name")}
        />
      ),
      cell: ({ row: { original: contract } }) => {
        return <p>{contract.name}</p>;
      },
    },
    {
      id: t("Contract.date"),
      accessorKey: "createdAt",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        return rowA.original.date.getTime() - rowB.original.date.getTime();
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Contract.date")} />
      ),
      cell: ({ row: { original: contract } }) => {
        return <p>{format(contract.date, "P")}</p>;
      },
    },
    {
      id: t("Contract.signed_on"),
      accessorKey: "createdAt",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        const timeA = rowA.original.signedAt?.getTime() ?? 0;
        const timeB = rowB.original.signedAt?.getTime() ?? 0;
        return timeA - timeB;
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Contract.signed_on")}
        />
      ),
      cell: ({ row: { original: contract } }) => (
        <>
          {contract.signedAt ? (
            <p>{format(contract.signedAt, "Pp")}</p>
          ) : (
            t("Contract.not_signed_yet")
          )}
        </>
      ),
    },
    // {
    //   id: "Options",
    //   enableSorting: false,
    //   size: 10,
    //   header: ({ column }) => (
    //     <DataTableColumnHeader
    //       title="Options"
    //       className="justify-end"
    //       column={column}
    //     />
    //   ),
    //   cell: ({ row: { original: contract } }) => {
    //     return (
    //       <div className="flex justify-end">
    //         <Button
    //           onClick={() => {
    //             setSelectedContract(contract);
    //           }}
    //           variant="ghost"
    //         >
    //           <Download size={20} />
    //         </Button>
    //       </div>
    //     );
    //   },
    // },
  ];

  useEffect(() => {
    if (selectedContract && downloadUrl) {
      // Create a temporary URL for the blob
      const byteString = atob(downloadUrl.base64PDF.split(",")[1]); // Remove the `data:application/pdf;base64,` prefix
      const byteArray = new Uint8Array(
        Array.from(byteString).map((char) => char.charCodeAt(0)),
      );
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = URL.createObjectURL(blob);

      // Create an anchor element and trigger a download
      const a = document.createElement("a");
      a.href = url;
      a.download = `Contract-${selectedContract.name}.pdf`; // Specify the file name
      document.body.appendChild(a); // Append to the DOM temporarily
      a.click(); // Trigger the download
      a.remove(); // Remove the element

      // Release the temporary URL
      URL.revokeObjectURL(url);
      setSelectedContract(undefined);
    }
  }, [downloadUrl, selectedContract]);

  useEffect(() => {
    if (!currentEmployee) {
      setPermission(false);
    }
    hasPermission({
      requiredPermission: Permissions.view_billing,
      label: t("Permissions.view_contracts_label"),
    })
      .then((result) => {
        setPermission(result.permission);
      })
      .catch((e) => console.error(e));
  }, [hasPermission, currentEmployee, t]);

  if (!permission) return null;
  return (
    <>
      <SettingsHeader title={t("Account.contracts_page_title")} />
      <DataTable
        data={data ?? []}
        columns={columns}
        onSelect={(contract) => {
          handleGenerateAccessLink(contract.id.toString());
        }}
      />
    </>
  );
}
